// ─────────────────────────────────────────────
//  Sandbox seed — turns the onboarding wizard's answers into a board that
//  actually looks like an OR board.
//
//  WHAT IS REAL AND WHAT IS NOT: the rooms are the user's own — they typed the
//  count and they own the names. Only the PEOPLE and the DAY are fictional, and
//  every fictional record carries `sample: true` so it can be deleted from the
//  normal UI (Setup → Staff, the daily grid) without a special "reset demo"
//  button. An OR board with nothing on it is the weakest possible demonstration
//  of an OR board, which is why the eight sample providers exist at all.
//
//  IDEMPOTENCE IS A REQUIREMENT, NOT A NICETY. "Open my board" is reachable
//  more than once (back button, resumed wizard, a second visit before the
//  profile flips to onboarded), so every document is written with a
//  DETERMINISTIC id via setDoc — `sbx-room-1`, `sbx-staff-alvarez`. addDoc
//  would silently double the roster on the second press.
//
//  WHY THE PRIMITIVES COME FROM ./localDb AND NOT firebase/firestore:
//  `src/firebase/firestore.js` imports the REAL Firebase SDK, and the real SDK
//  rejects the sandbox store outright —
//    doc(sandboxDb, "rooms", "r1")
//      → "Expected first argument to collection() to be a CollectionReference,
//         a DocumentReference or FirebaseFirestore"
//  — because it type-checks `parent instanceof Firestore`. There is no module
//  alias mapping `firebase/firestore` onto the shim, so calling createRoom() /
//  addStaff() / createAssignment() from here would throw on the first write.
//  Their FIELD DEFAULTS still matter to the board, so they are mirrored below
//  field-for-field, each with the helper it came from named in a comment. If
//  those helpers ever change, change these with them.
// ─────────────────────────────────────────────
import { db } from "../firebase/config";
import { doc, getDoc, setDoc, serverTimestamp } from "./localDb";
import { SANDBOX_FACILITY_ID, SANDBOX_FACILITY_NAME } from "../config/roles";
import { rosterDateKey } from "../hooks/useDailyRoster";

// A pathological orCount (a fat-fingered 99999) would spend the demo writing
// rooms into IndexedDB and never render. Clamped, not rejected: the user still
// gets a board.
const MAX_SEEDED_ROOMS = 60;

// The first three rooms get a story so the board reads as a live day rather
// than a grid of identical empty cards. Rooms past these stay "empty".
const OPENING_STATUSES = ["active", "ready", "closing"];

// Eight fictional providers. `role` is the STAFF vocabulary domain/staff.js
// splits the pool on ("doctor" | "crna" | "aa" | "resident"); `subRole` is the
// clinical label the sample data is described by. Both are written because the
// pool split reads `role` while the seed contract is expressed in `subRole`.
//
// ⚠️ EVERY NUMBER BELOW IS A 555 NUMBER AND EVERY ADDRESS IS example.test.
// 555-01xx is the block reserved for fiction, and `.test` is an RFC 2606
// reserved TLD that can never be registered, so a mis-click dials nobody and
// mails nowhere. The sandbox is the ONLY place sample contact details are
// written — `facility default` is AdventHealth's live board and every
// clinician on it is a real person (see scripts/seedDirectoryContacts.mjs,
// which refuses to invent anything).
const SAMPLE_STAFF = [
  { key: "alvarez",  name: "Dr. Alvarez",  role: "doctor", subRole: "md",   phone: "(555) 0100-101", email: "alvarez@example.test" },
  { key: "bennett",  name: "Dr. Bennett",  role: "doctor", subRole: "md",   phone: "(555) 0100-102", email: "bennett@example.test" },
  { key: "chen",     name: "Dr. Chen",     role: "doctor", subRole: "md",   phone: "(555) 0100-103", email: "chen@example.test" },
  { key: "diaz",     name: "R. Diaz",      role: "crna",   subRole: "crna", phone: "(555) 0100-201", email: "diaz@example.test" },
  { key: "ellis",    name: "M. Ellis",     role: "crna",   subRole: "crna", phone: "(555) 0100-202", email: "ellis@example.test" },
  // Opted OUT of the directory. A sandbox that only ever shows listed people
  // proves nothing about the filter — this row is what makes `listed === false`
  // visibly do something in a running board rather than only in a unit test.
  { key: "fontaine", name: "J. Fontaine",  role: "crna",   subRole: "crna", phone: "(555) 0100-203", email: "fontaine@example.test", listed: false },
  { key: "grant",    name: "K. Grant",     role: "aa",     subRole: "aa",   phone: "(555) 0100-301", email: "grant@example.test" },
  { key: "hayes",    name: "T. Hayes",     role: "aa",     subRole: "aa",   phone: "(555) 0100-302", email: "hayes@example.test" },
];

// Three surgeons, using the SAME names the sample assignments already put on
// the board — a directory that lists surgeons nobody in the room has heard of
// reads as decoration rather than as the department's own record.
const SAMPLE_SURGEONS = [
  { key: "whitfield", name: "Dr. Whitfield", specialty: "Orthopedics",     phoneNumber: "5550100401", phoneDisplay: "(555) 0100-401", email: "whitfield@example.test" },
  { key: "osei",      name: "Dr. Osei",      specialty: "General Surgery", phoneNumber: "5550100402", phoneDisplay: "(555) 0100-402", email: "osei@example.test" },
  { key: "laurent",   name: "Dr. Laurent",   specialty: "Neurosurgery",    phoneNumber: "5550100403", phoneDisplay: "(555) 0100-403", email: "laurent@example.test" },
];

const staffId = (key) => `sbx-staff-${key}`;
const roomId = (n) => `sbx-room-${n}`;
const surgeonId = (key) => `sbx-surgeon-${key}`;

// One MD supervising one CRNA/AA, in room order. Capped by the number of MDs —
// three staffed rooms is enough to show supervision, breaks and status colour.
const SAMPLE_PAIRS = [
  { md: "alvarez", ahp: "diaz",     start: "07:30", end: "11:00", surgeon: "Dr. Whitfield" },
  { md: "bennett", ahp: "ellis",    start: "07:30", end: "12:30", surgeon: "Dr. Osei" },
  { md: "chen",    ahp: "fontaine", start: "08:00", end: "10:30", surgeon: "Dr. Laurent" },
];

const byKey = (key) => SAMPLE_STAFF.find((s) => s.key === key);

// AssignmentModal's providerRole convention: the sub-role in caps.
const providerRoleLabel = (person) => (person.subRole || person.role || "").toUpperCase();

// ── FACILITY ─────────────────────────────────
// Mirrors firestore.createFacility() on first run. On a re-seed it MERGES the
// name instead of replacing the document, because by then the user may have
// added locations or specialties from Setup and a full overwrite would silently
// discard them.
async function seedFacility(orgName) {
  const ref = doc(db, "facilities", SANDBOX_FACILITY_ID);
  const name = (orgName || "").trim() || SANDBOX_FACILITY_NAME;
  const existing = await getDoc(ref);

  // The facility's own contact row on the Team page (v2 §1.4). Merged on a
  // re-seed alongside the name for the same reason the name is: by then the
  // user may have typed their real switchboard number into Setup, and a full
  // overwrite would silently replace it with fiction.
  const contact = {
    phoneNumber: "5550100001",
    phoneDisplay: "(555) 0100-001",
    email: "front.desk@example.test",
    address: "1 Sample Way, Demo City, FL 00000",
  };

  if (existing.exists()) {
    await setDoc(ref, { name, ...contact, updatedAt: serverTimestamp() }, { merge: true });
    return;
  }
  await setDoc(ref, {
    // firestore.createFacility defaults
    isActive: true,
    locations: [],
    specialties: [],
    name,
    ...contact,
    facilityId: SANDBOX_FACILITY_ID,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// ── SURGEONS ─────────────────────────────────
// Mirrors firestore.addSurgeon()'s defaults, deterministic ids for the same
// idempotence reason as everything else here. Tagged `sample: true` so the
// fictional three can be deleted from the normal UI once real records exist.
async function seedSurgeons() {
  for (const sg of SAMPLE_SURGEONS) {
    await setDoc(doc(db, "surgeons", surgeonId(sg.key)), {
      // firestore.addSurgeon defaults
      name: sg.name,
      phoneNumber: sg.phoneNumber,
      phoneDisplay: sg.phoneDisplay,
      email: sg.email,
      specialty: sg.specialty,
      facilityId: SANDBOX_FACILITY_ID,
      listed: true,
      sample: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
}

// ── ROOMS ────────────────────────────────────
// Mirrors firestore.createRoom()'s defaults, then writes the live assignment
// straight onto the room doc the way AssignmentModal does via setRoomAssignment
// — the board renders from the ROOM doc, so a room without those fields shows
// "Empty" even when an assignment row exists.
async function seedRooms(count) {
  for (let n = 1; n <= count; n += 1) {
    const pair = SAMPLE_PAIRS[n - 1];
    const md = pair ? byKey(pair.md) : null;
    const ahp = pair ? byKey(pair.ahp) : null;

    await setDoc(doc(db, "rooms", roomId(n)), {
      // firestore.createRoom defaults
      status: OPENING_STATUSES[n - 1] || "empty",
      providerId: ahp ? staffId(ahp.key) : null,
      supervisingDoctorId: md ? staffId(md.key) : null,
      comments: "",
      isActive: true,
      onBreak: false,
      // the room itself — the user's, not sample data, so NOT tagged sample
      facilityId: SANDBOX_FACILITY_ID,
      name: `OR ${n}`,
      // ⚠️ main_or, NOT "". The wizard's question is literally "How many
      // OPERATING ROOMS?", so filing every answer as unclassified put a brand
      // new board's entire room list under a section headed "Other" — and the
      // give-a-break picker, which groups by this same key, did the same. An
      // empty location is for a room somebody deliberately left unsorted, not
      // for the eight rooms the product just asked them to count.
      location: "main_or",
      // setRoomAssignment mirror (AssignmentModal)
      providerName: ahp ? ahp.name : "",
      providerRole: ahp ? providerRoleLabel(ahp) : "",
      supervisorName: md ? md.name : "",
      scheduledStartTime: pair ? pair.start : "",
      updatedAt: serverTimestamp(),
    });
  }
}

// ── STAFF ────────────────────────────────────
// Mirrors firestore.addStaff()'s defaults. Every one is tagged `sample: true`
// so the user can delete the fictional team from Setup → Staff once their own
// people exist.
async function seedStaff() {
  for (const person of SAMPLE_STAFF) {
    await setDoc(doc(db, "staff", staffId(person.key)), {
      // firestore.addStaff defaults
      name: person.name,
      phone: person.phone,
      email: person.email,
      // Absent means listed, so only the opted-out row carries the flag as
      // false — writing `listed: true` everywhere would hide the fact that the
      // absent case is the one every pre-existing record takes.
      listed: person.listed !== false,
      role: person.role,
      primaryFacilityId: SANDBOX_FACILITY_ID,
      employmentType: "permanent",
      uid: null, // pool-only: a sample provider has no login and cannot be alerted
      subRole: person.subRole,
      facilityId: SANDBOX_FACILITY_ID,
      facilityIds: [SANDBOX_FACILITY_ID],
      shortCode: null,
      sample: true,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
}

// ── ASSIGNMENTS ──────────────────────────────
// Mirrors the row shape AssignmentModal / AssignmentGrid save plus
// firestore.createAssignment()'s status + createdAt.
//
// `orRoom` is the room name with the "OR " prefix stripped, because that is what
// domain/gridTemplate.assignmentForRoom() matches on — an orRoom of "OR 1" and
// a room named "OR 1" still match, but "1" is the shape the rest of the app
// writes, and matching the app matters more than matching the label.
//
// The day key comes from rosterDateKey() — the project's single source of truth
// for the LOCAL calendar day. A hand-rolled UTC string files the seeded day
// under tomorrow all evening in US time zones, and the board then shows nothing.
async function seedAssignments(count, uid) {
  const date = rosterDateKey();

  for (let n = 1; n <= Math.min(count, SAMPLE_PAIRS.length); n += 1) {
    const pair = SAMPLE_PAIRS[n - 1];
    const md = byKey(pair.md);
    const ahp = byKey(pair.ahp);

    await setDoc(doc(db, "assignments", `sbx-asgn-${date}-room-${n}`), {
      facilityId: SANDBOX_FACILITY_ID,
      date,
      orRoom: String(n),
      roomId: roomId(n),
      surgeon: pair.surgeon,
      startTime: pair.start,
      endTime: pair.end,
      attendingId: staffId(md.key),
      attendingName: md.name,
      ahpId: staffId(ahp.key),
      ahpName: ahp.name,
      ahpShiftCode: null,
      ahpDepartTime: "",
      crnaReliefId: null,
      crnaReliefName: "",
      mdReliefId: null,
      mdReliefName: "",
      source: "sandbox-sample",
      sample: true,
      // firestore.createAssignment defaults
      status: "scheduled",
      createdAt: serverTimestamp(),
      updatedBy: uid || null,
    });
  }
}

/**
 * Build the sandbox board from the wizard's answers.
 *
 * @param {object}  args
 * @param {string}  args.uid          the signed-in sandbox user, stamped as the actor
 * @param {object}  args.onboarding   wizard answers: { orgName, facilityType, orCount, teamSize }
 * @returns {Promise<void>}
 */
// ── THE DAY'S ROSTER ─────────────────────────
// ⚠️ WITHOUT THIS, HALF THE PRODUCT IS INVISIBLE ON THE DEMO BOARD. Rooms,
// staff and assignments were seeded and the daily_boards doc was not, so
// everything derived from the ROSTER rather than from the rooms came up empty:
// BREAKS read "Nobody on shift with a break record today" and FREE NOW read
// "everyone on shift is in a room" — while three CRNAs sat visibly in rooms two
// inches away. A front door that contradicts itself is worse than a bare one.
//
// The AIC is the first sample doctor: a board with no AIC hides the controls
// that need one, and "nobody is in charge" is not the state a demonstration
// should open in.
//
// Shape mirrors setDailyRoster's own writes (hooks/useDailyRoster reads exactly
// these keys, and drops any it does not name).
async function seedRoster() {
  const date = rosterDateKey();
  const doctors = SAMPLE_STAFF.filter((p) => p.role === "doctor");
  const providers = SAMPLE_STAFF.filter((p) => p.role !== "doctor");
  const aic = doctors[0] || null;

  await setDoc(doc(db, "daily_boards", `${SANDBOX_FACILITY_ID}_${date}`), {
    facilityId: SANDBOX_FACILITY_ID,
    date,
    aicId: aic ? staffId(aic.key) : null,
    aicName: aic ? aic.name : "",
    supervisors: doctors.map((d) => ({ id: staffId(d.key), name: d.name })),
    crnas: providers.map((p) => ({
      id: staffId(p.key),
      name: p.name,
      subRole: p.subRole || "crna",
    })),
    departed: {},
    givingBreaks: {},
    crnaCallList: [],
    mdCallList: [],
    mdCallHome: [],
    locumLeaveTimes: {},
    hiddenRooms: [],
    sample: true,
    updatedBy: null,
    updatedAt: serverTimestamp(),
  });
}

export async function seedSandbox({ uid, onboarding } = {}) {
  const answers = onboarding || {};

  // A MISSING count seeds zero rooms and does not throw. Number(undefined) is
  // NaN and `n <= NaN` is false, so the loop would be harmless — but
  // Number("4abc") is NaN too and Number(null) is 0, and neither is a room
  // count anybody typed. Fail closed on anything that is not a whole number.
  const requested = Number.isInteger(answers.orCount) ? answers.orCount : 0;
  const roomCount = Math.max(0, Math.min(requested, MAX_SEEDED_ROOMS));

  await seedFacility(answers.orgName);
  await seedStaff();
  await seedSurgeons();
  await seedRooms(roomCount);
  await seedAssignments(roomCount, uid);
  await seedRoster();
}

export default seedSandbox;
